// The navigation overlay's numbers (key N): where the observer is among the bodies' pulls, what orbit it is on, and the Lagrange points about it. No three.js here.
//
// Everything is the bodies' (bodies.ts, gravity.ts), in the simulation's own time: speeds are metres per second of the sky's time (the bodies message's seconds per tick), so a faster
// simulation is a faster reading, as it is a faster sky.
import { len, mulv, sub } from './bodies';
import type { Bodies, V3 } from './bodies';
import { lagrangePoints, orbitElements, strongest } from './gravity';
import type { OrbitElements } from './gravity';
import type { Flight } from './flight';
import type { Well } from './lattice';

export interface NavMark {
  /** "Sun-Home L4": the pair and the point. */
  name: string;
  /** Unit direction from the observer in the render axes (x east, y away from the followed body, z south), and the distance (m). */
  dir: V3;
  dist: number;
}

export interface NavInfo {
  /** The thrusters are on (the observer has a velocity of its own); else the controls move it directly. */
  dynamic: boolean;
  /** The body that pulls hardest here, and its pull (m/s^2). */
  pullBody: string;
  pull: number;
  /** The height above that body's surface (m). */
  alt: number;
  /** Speed relative to that body (m/s), and the orbit it makes about it; null when the controls move the observer directly. */
  speed: number | null;
  orbit: (OrbitElements & { periAlt: number; apoAlt: number; periodS: number }) | null;
  /** The thrust now (m/s^2). */
  thrust: number;
  /** The nearest Lagrange point, and all of them in the render axes. */
  nearest: NavMark | null;
  marks: NavMark[];
}

const pretty = (id: string): string => id.charAt(0).toUpperCase() + id.slice(1);

/** The overlay's numbers for this frame, null when the observer is not out in space among the server's bodies. `marks`: also work out the Lagrange points. */
export function navInfo(B: Bodies, flight: Flight, tick: number, marks: boolean): NavInfo | null {
  const pose = flight.pose;
  const p = flight.absPosition(tick);
  if (!pose || !p) return null;
  const s2 = B.secPerTick * B.secPerTick;
  const abs = B.absAll(tick);
  const st = strongest(B, abs, p);
  if (st.i < 0) return null;
  const body = B.list[st.i];
  const rel = sub(p, abs[st.i]);
  const o = flight.orbiter;
  let speed: number | null = null;
  let orbit: NavInfo['orbit'] = null;
  if (o) {
    const v = sub(o.vel, B.absVel(st.i, tick));
    speed = len(v) / B.secPerTick;
    const el = orbitElements(body.gm ?? 0, rel, v);
    orbit = { ...el, periAlt: el.peri - body.radius, apoAlt: el.apo - body.radius, periodS: el.period * B.secPerTick };
  }
  const list: NavMark[] = [];
  let nearest: NavMark | null = null;
  if (marks) {
    for (const lp of lagrangePoints(B, abs, tick)) {
      const d = sub(lp.pos, p);
      const dist = len(d);
      if (!(dist > 0)) continue;
      const r = mulv(pose.axes, d);
      const m: NavMark = { name: `${pretty(B.list[lp.parent].id)}-${pretty(B.list[lp.child].id)} L${lp.n}`, dir: [r[0] / dist, r[1] / dist, r[2] / dist], dist };
      list.push(m);
      if (!nearest || dist < nearest.dist) nearest = m;
    }
  }
  return { dynamic: !!o, pullBody: pretty(body.id), pull: st.a / s2, alt: len(rel) - body.radius, speed, orbit, thrust: flight.thrust / s2, nearest, marks: list };
}

/** A distance for a reading: metres to a few thousand km, then km, then millions of km. */
export function fmtDist(m: number): string {
  const a = Math.abs(m);
  if (!Number.isFinite(a)) return '∞';
  if (a < 1e4) return `${m.toFixed(0)} m`;
  if (a < 1e7) return `${(m / 1e3).toFixed(a < 1e5 ? 1 : 0)} km`;
  if (a < 1e10) return `${(m / 1e6).toFixed(a < 1e8 ? 2 : 1)} Mm`;
  return `${(m / 1.495978707e11).toFixed(3)} AU`;
}

/** A time in seconds for a reading. */
export function fmtTime(s: number): string {
  if (!Number.isFinite(s)) return '∞';
  if (s < 120) return `${s.toFixed(0)} s`;
  if (s < 7200) return `${(s / 60).toFixed(1)} min`;
  if (s < 172800) return `${(s / 3600).toFixed(1)} h`;
  return `${(s / 86400).toFixed(1)} d`;
}

/** A speed (m/s). */
export function fmtSpeed(v: number): string {
  const a = Math.abs(v);
  return a < 1e3 ? `${v.toFixed(1)} m/s` : `${(v / 1e3).toFixed(a < 1e5 ? 2 : 0)} km/s`;
}

/** What the lattice (lattice.ts) is drawn from: the observer's place, the bodies that pull (the home planet is the unit of the funnels' widths) and the distance to the one that pulls hardest. */
export function latticeInputs(B: Bodies, flight: Flight, tick: number): { obs: V3; wells: Well[]; home: Well; scale: number; axes: ArrayLike<number> } | null {
  const pose = flight.pose;
  const obs = flight.absPosition(tick);
  if (!pose || !obs) return null;
  const abs = B.absAll(tick);
  const wells: Well[] = [];
  let home: Well | null = null;
  for (let i = 0; i < B.list.length; i++) {
    const b = B.list[i];
    if (!b.gm) continue;
    const w: Well = { pos: abs[i], radius: b.radius, gm: b.gm };
    wells.push(w);
    if (i === 0) home = w;
  }
  const st = strongest(B, abs, obs);
  if (!home || st.i < 0) return null;
  return { obs, wells, home, scale: Math.max(len(sub(obs, abs[st.i])), 1), axes: pose.axes };
}
